import React, { useState, useEffect, useMemo } from 'react';
import MatchCard from "../../MatchCard/MatchCard";
import RoundTabs from "../../RoundTabs/RoundTabs";
import SectionTitle from "../../SectionTitle/SectionTitle";
import smallShield from '../../../assets/smallShield.png';
import './styles.scss'
import { useTranslation } from 'hooks/useTranslation';
import { TRANSLATION_KEYS } from '../GroupStage/constants';
import { formatMatchTimeByRegion } from '../../../../utils/formatMatchTime';
import { getRegion } from 'utils/Region';
import { useMundialMetrics } from '../../../../hooks/useMundialMetrics';
import { INTERACTION_TYPES } from '../../../../hooks/useMundialMetrics/constants';
import { ACTIONABLE_MATCH_STATUSES } from '../../../../hooks/useMundialMetrics/utils/buildMatchValues';
import { useMundialScrollTracker } from '../../../../hooks/useMundialScrollTracker';
import { getVisibleMatchNames } from '../../../utils/getVisibleMatchNames';

const QF_MOBILE_MEDIA = '(max-width: 1024px)';

function formatMatchTime(m, isMobile) {
  return formatMatchTimeByRegion(m, {
    includeDate: true,
    bracketSingleLine: !isMobile,
  });
}

export default function QuartersStage({ stageData, nextStageData, mobileMenuOpen, onMobileRoundMenuOpenChange, activeRound, onRoundChange, onMatchCardClick  }) {
  const { translationMapper } = useTranslation();
          const translatedTexts = useMemo(
              () => translationMapper(TRANSLATION_KEYS),
              [],
            );
  
  
  const rounds = stageData?.data?.rounds || [];
  const stageName = translatedTexts['TITLE_1_QUARTER_FINALS'];
  const nextStageName = translatedTexts['TITLE_2_QUARTER_FINALS'];
  const round = activeRound || 0;
  const activeRoundData = rounds[round];
  const matches = activeRoundData?.matches || [];
  const nextMatchId = matches[0]?.nextMatchId;

  const nextMatch = useMemo(() => {
    if (!nextMatchId || !nextStageData?.data?.rounds) return null;
    for (const r of nextStageData.data.rounds) {
      const found = r.matches.find(m => m.matchId === nextMatchId);
      if (found) return found;
    }
    return null;
  }, [nextMatchId, nextStageData]);
  const { trackSelectContent } = useMundialMetrics();
  const setRound = (r) => {
    const moduloName = `ronda ${r + 1}`;
    trackSelectContent({
      pageTitleSegments: ['cuartos', moduloName],
      pageTitle: moduloName,
      contentSubsection: 'cuartos',
      moduloName,
      interactionType: INTERACTION_TYPES.GROUP_TABS,
    });
    onRoundChange?.(String(r));
  };

  useMundialScrollTracker({
    pageTitleSegments: ['cuartos', `ronda ${round + 1}`],
    contentSubsection: 'cuartos',
    moduloName: `ronda ${round + 1}`,
    getVisibleContentIds: getVisibleMatchNames,
  });

  const handleMatchClick = (match) => {
    onMatchCardClick?.(match.matchId);
    if (!ACTIONABLE_MATCH_STATUSES.includes(match.status)) return;
    const moduloName = `ronda ${round + 1}`;
    const matchName = `${match.homeTeam?.name || ''} vs ${match.awayTeam?.name || ''}`;
    trackSelectContent({
      pageTitleSegments: ['cuartos', moduloName],
      pageTitle: matchName,
      contentSubsection: 'cuartos',
      moduloName,
      interactionType: INTERACTION_TYPES.CARD_SELECT,
      match,
    });
  };

  const [isQfMobileLayout, setIsQfMobileLayout] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(QF_MOBILE_MEDIA).matches : false
  );

  useEffect(() => {
    const mq = window.matchMedia(QF_MOBILE_MEDIA);
    const sync = () => setIsQfMobileLayout(mq.matches);
    sync();
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', sync);
      return () => mq.removeEventListener('change', sync);
    }
    mq.addListener(sync);
    return () => mq.removeListener(sync);
  }, []);

  return (
    <div className="quarters-stage">
      <RoundTabs
        rounds={rounds.map((r, i) => `Ronda ${i + 1}`)}
        active={round}
        onChange={setRound}
        mobileMenuOpen={mobileMenuOpen}
        onMobileMenuOpenChange={onMobileRoundMenuOpenChange}
      />

      {activeRoundData ? (
        <div className="qf-content">
          <div className="qf-column qf-column--left qf-column--left-desktop-offset">
            <div className="qf-section-title">
              <SectionTitle>{stageName}</SectionTitle>
            </div>
            <div className="qf-matches">
              {matches.map((m) => (
                <MatchCard
                  key={m.matchId}
                  time={formatMatchTime(m, isQfMobileLayout)}
                  teamA={m.homeTeam.name}
                  teamB={m.awayTeam.name}
                  shortNameA={m.homeTeam.shortName}
                  shortNameB={m.awayTeam.shortName}
                  flagUrlA={m.homeTeam.flagUrl}
                  flagUrlB={m.awayTeam.flagUrl}
                  stadium={`${m.venue.name}, ${m.venue.city}`}
                  status={m.status}
                  score={m.score}
                  matchMinute={m.matchProgress?.minute}
                  isBroadcastAvailable={m.broadcast?.isAvailable}
                  lineLeft
                  lineLeftWidth={136}
                  abbreviateTeamNamesOnMobile
                  noFlagPlaceholderSrc={smallShield}
                  onClick={onMatchCardClick ? () => handleMatchClick(m) : undefined}
                />
              ))}
            </div>
          </div>

          <div className="qf-connectors" aria-hidden="true">
            <div className="qf-bracket qf-bracket--left" />
          </div>

          <div className="qf-column qf-column--center">
            <div className="qf-section-title">
              <SectionTitle>{nextStageName}</SectionTitle>
            </div>
            <div className="qf-center-match">
              <MatchCard
                time={nextMatch ? formatMatchTime(nextMatch, isQfMobileLayout) : 'Por definir'}
                teamA={nextMatch?.homeTeam?.name || 'Por definir'}
                teamB={nextMatch?.awayTeam?.name || 'Por definir'}
                shortNameA={nextMatch?.homeTeam?.shortName}
                shortNameB={nextMatch?.awayTeam?.shortName}
                flagUrlA={nextMatch?.homeTeam?.flagUrl}
                flagUrlB={nextMatch?.awayTeam?.flagUrl}
                stadium={nextMatch ? `${nextMatch.venue.name}, ${nextMatch.venue.city}` : ''}
                status={nextMatch?.status}
                score={nextMatch?.score}
                matchMinute={nextMatch?.matchProgress?.minute}
                isBroadcastAvailable={nextMatch?.broadcast?.isAvailable}
                lineLeft
                lineLeftWidth={165}
                lineRight
                lineRightWidth={139}
                abbreviateTeamNamesOnMobile
                noFlagPlaceholderSrc={smallShield}
                onClick={
                  onMatchCardClick && nextMatch
                    ? () => handleMatchClick(nextMatch)
                    : undefined
                }
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
